import moment from "moment"

/**
 * « Combien d'heures coûte un mois partiel » — le miroir mobile de la règle,
 * unique côté app.
 *
 * LA RÈGLE : `round((heures_du_mois × jours_couverts / jours_du_mois) + 0.25)`.
 * Le `+0.25` est un biais délibérément à la baisse : il n'arrondit au-dessus
 * qu'à partir de .75, pour qu'une famille ne paie jamais une fraction d'heure
 * qu'elle atteint à peine.
 *
 * ⚠️ MIROIR — la maison de cette règle est côté back :
 * `App\Services\FSM\Subscription\Domain\PartialMonthHoursDomain::hoursFor()`.
 * C'est lui qui décide le montant réellement prélevé ; cette fonction ne fait
 * qu'annoncer le même nombre au parent AVANT qu'il accepte. Les deux doivent
 * donc dire la même chose : `PartialMonthHoursDomainTest` porte une table dorée
 * dont `partialMonthHours.test.js` reprend les lignes, précisément pour qu'une
 * divergence échoue bruyamment des deux côtés.
 *
 * Il reste une maison par runtime — back (PHP), app (ici), BO
 * (`bo-web/src/data/partialMonthHours.ts`) — parce qu'aucun package JS n'est
 * partagé entre l'app et le BO. Trois miroirs testés contre la même table, au
 * lieu des cinq copies dont deux arrondissaient différemment.
 *
 * @param {string|Date|import("moment").Moment} startsAt Premier jour couvert.
 * @param {number} hoursPerMonth Heures que vaut un mois plein de cette formule.
 * @returns {number|undefined} Heures à facturer, `undefined` si l'entrée est inexploitable.
 */
export const partialMonthHours = (startsAt, hoursPerMonth) => {
    if (!startsAt || !hoursPerMonth) return undefined

    // `moment(string)` sans format déprécie les formats non ISO ; les appelants
    // passent soit "YYYY-MM-DD HH:mm", soit une date déjà parsée.
    const start =
        typeof startsAt === "string"
            ? moment(startsAt, ["YYYY-MM-DD HH:mm", moment.ISO_8601])
            : moment(startsAt)
    if (!start.isValid()) return undefined

    const daysInMonth = start.daysInMonth()
    const coveredDays = start.clone().endOf("month").diff(start, "days") + 1

    return Math.round((hoursPerMonth * coveredDays) / daysInMonth + 0.25)
}
